// Moved to PromotionCampaignDetail/PromotionCampaignDetail.jsx
import React, { useState } from 'react';
import { Dimmer, Form, Placeholder } from 'semantic-ui-react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom-v5-compat';
import { Table, Label, Segment, Button, Pagination, Checkbox, Input } from 'semantic-ui-react';
import { EditableBarcodes } from './EditableBarcodes/EditableBarcodes';
import DisplayMecanism from './DisplayMecanism/DisplayMecanism';
import { useMutation } from '@tanstack/react-query';
import { updatePromotionCampaign, updatePromotionCampaignPromotions } from '../../../services/PromotionCampaign/PromotionCampaign.service';
import { EditablePromotionStatus } from './EditablePromotionStatus/EditablePromotionStatus';
import { useCampaignsReload } from '../_hooks/useCampaigns';
import { useTranslation } from 'react-i18next';


const SourceText = ({source,externalId}) => {
  const text = [source,externalId].filter(Boolean)
  if(text.length === 0) return <span>-</span>;
  return <span>{text.join(' | ')}</span> ;
}



const PromotionCampaignDetail = ({ campaign, isLoading }) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10; // You can adjust this as needed
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const reload = useCampaignsReload();
  const { status } = campaign || {};
  const { currentFilters } = useOutletContext();

  // Filter state
  const [hideDisabled, setHideDisabled] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');

  const updatePromotionCampaignMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const result = await updatePromotionCampaign(id, { status });
      reload();
      return result;
    },
    onMutate: ({ status }) => {
      campaign.status = status;
    },
  });

  const isEditable = campaign && !isLoading && campaign?.status !== 'COMPLETED';

  // Filtering logic
  let promotions = campaign?.promotions || [];
  // Count DRAFT promotions (before filtering)
  const draftPromotions = (promotions || []).filter((promo) => promo.status === 'DRAFT')
  if (hideDisabled) {
    promotions = promotions.filter((promo) => promo.status !== 'DISABLED');
  }
  if (searchFilter.trim() !== '') {
    const filterValue = searchFilter.trim().toLowerCase();
    promotions = promotions.filter((promo) =>
      (promo.externalId || '').toLowerCase().includes(filterValue) ||
      (promo.barcodes || []).some((b) => b.toLowerCase().includes(filterValue))
    );
  }

  // Dummy function for disabling all DRAFT promotions
  const disableAllPromotionDrafts = useMutation({
    mutationFn: async (draftProms) => {
      
      await updatePromotionCampaignPromotions(campaign.id, {
          ids: draftProms.map(p => p.id),
          patch: {
            status: 'DISABLED'
          }
        });
      setHideDisabled(true);
      reload();
    },
    onMutate: (draftProms) => {
      //Optimistic update
      for(const promo of draftProms) {
        promo.status = 'DISABLED';
      }
    }
  })

  // Pagination logic
  const totalPages = Math.ceil(promotions.length / pageSize);
  const paginatedPromotions = promotions.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <Segment isLoading={isLoading} style={{ height: 'calc(100% - 100px)', display: 'flex', flexDirection: 'column', minHeight: 500 }}>
      <div style={{ flex: 'none' }}>
        <Button
          basic
          size="tiny"
          icon="arrow left"
          content={t('promotion_campaign.back_to_list', 'Revenir à la liste')}
          onClick={() => navigate(`/categories/promotion-campaigns${location.search}`)}
          style={{ marginBottom: 16 }}
        />
        <br />
        {isEditable && (
          <Button.Group size="large">
            {status !== 'ENABLED' && (
              <Button
                loading={updatePromotionCampaignMutation.isPending}
                color="teal"
                onClick={() => updatePromotionCampaignMutation.mutate({ id: campaign.id, status: 'ENABLED' })}
              >
                {t('promotion_campaign.enable', 'Activer la campagne')}
              </Button>
            )}
            {status !== 'DISABLED' && (
              <Button
                loading={updatePromotionCampaignMutation.isPending}
                color="grey"
                basic
                onClick={() => updatePromotionCampaignMutation.mutate({ id: campaign.id, status: 'DISABLED' })}
              >
                {t('promotion_campaign.disable', 'Désactiver la campagne')}
              </Button>
            )}
          </Button.Group>
        )}

        <h3>{t('promotion_campaign.promotions.header', 'Promotions')}</h3>

        {/* Filter controls */}
         <Form>
            <Form.Group  style={{
      display: 'flex',
      alignItems: 'center', // vertical alignment
      gap: '12px',          // spacing between elements
    }}>
              <Form.Field width={6}>
          <Checkbox
            toggle
            checked={hideDisabled}
            onChange={() => setHideDisabled((v) => !v)}
            label={!hideDisabled ? t('promotion_campaign.promotions.hide_disabled', 'Masquer les promotions désactivées') : t('promotion_campaign.promotions.show_disabled', 'Afficher les promotions désactivées')}
            style={{ marginRight: 8 }}
          />
          </Form.Field>
          <Form.Field  width={5}>
            <Input
              type="text"
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setCurrentPage(1);
              }}
              placeholder={t('promotion_campaign.promotions.search_filter_label', 'Filtrer par EAN, ID externe...')}
              style={{ marginLeft: 8, padding: 4 }}
            />
          </Form.Field>
          <Form.Field>
          {draftPromotions.length > 0 && (
            <Button
              color="orange"
              size="tiny"
              loading={disableAllPromotionDrafts.isPending}
              onClick={() => disableAllPromotionDrafts.mutate(draftPromotions)}
              style={{ marginLeft: 16 }}
            >
              {t('promotion_campaign.promotions.disable_all_draft', 'Désactiver les {{count}} promotions restantes', { count: draftPromotions.length })}
            </Button>
          )}
        </Form.Field>
        </Form.Group>
        </Form>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', marginBottom: 16 }}>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('promotion_campaign.promotions.source', 'Source')}</Table.HeaderCell>
              <Table.HeaderCell>{t('promotion_campaign.promotions.status', 'Status')}</Table.HeaderCell>
              <Table.HeaderCell>{t('promotion_campaign.promotions.tags', 'Tags')}</Table.HeaderCell>
              <Table.HeaderCell>{t('promotion_campaign.promotions.barcodes', 'Code barres')}</Table.HeaderCell>
              <Table.HeaderCell>{t('promotion_campaign.promotions.mechanism', 'Mécanique')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
          {isLoading && (
            <>
         <Table.Row key="pc-details-table-loading">
                <Table.Cell>
                  <Placeholder>
                    <Placeholder.Line length='medium' />
                  </Placeholder>
                </Table.Cell>
                <Table.Cell>
                  <Placeholder>
                    <Placeholder.Line length='medium' />
                  </Placeholder>                </Table.Cell>
                <Table.Cell>
                  <Placeholder>
                    <Placeholder.Line length='medium' />
                  </Placeholder>
                </Table.Cell>
                <Table.Cell>
                <Placeholder>
                    <Placeholder.Line length='medium' />
                  </Placeholder>                </Table.Cell>
                <Table.Cell>
                  <Placeholder>
                    <Placeholder.Line length='medium' />
                  </Placeholder>
                </Table.Cell>
              </Table.Row>
        </>
          )}
            {!isLoading && paginatedPromotions.map((promo) => (
              <Table.Row key={promo.id}>
                <Table.Cell>
                  <SourceText source={promo.source} externalId={promo.externalId} />
                </Table.Cell>
                <Table.Cell>
                  <EditablePromotionStatus onEdit={reload} readonly={!isEditable} promotion={promo} />
                </Table.Cell>
                <Table.Cell>{promo.tags?.join(', ')}</Table.Cell>
                <Table.Cell>
                  <EditableBarcodes onEdit={reload} readonly={!isEditable} promotion={promo} currentSearch={currentFilters?.search} />
                </Table.Cell>
                <Table.Cell>{promo.mecanism ? <DisplayMecanism mecanism={promo.mecanism} /> : '-'}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        {/* Pagination */}
        {promotions.length > pageSize && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
            <Pagination
              activePage={currentPage}
              totalPages={totalPages}
              onPageChange={(_, data) => setCurrentPage(data.activePage)}
              siblingRange={1}
              boundaryRange={1}
            />
          </div>
        )}
	  </div>
  </Segment>
 );
};

export default PromotionCampaignDetail;
